import React, { useEffect, useState } from "react";
import Card from "components/card/Card";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import Checkbox from "components/form/checkbox/Checkbox";
import { ButtonNew as Button } from "components/buttonNew";
import { IPayoutAccountForm } from "../../teeOnPaymentsTypes";
import SelectNew from "components/select/SelectNew";
import { t } from "i18next";
import BankAccountDisplay from "../BankAccountDisplay";
import {
  isValidTransitNumber,
  isValidInstitutionNumber,
  isValidAccountNumber,
  isValidRoutingNumber,
} from "../../teeOnPaymentsValidation";

const defaultPayoutAccount: IPayoutAccountForm = {
  currency: "",
  accountNumber: "",
  transitNumber: "",
  institutionNumber: "",
  routingNumber: "",
  isDefault: false,
};

const defaultDirty = {
  currency: false,
  accountNumber: false,
  transitNumber: false,
  institutionNumber: false,
  routingNumber: false,
};

interface IPayoutConfigurationStepProps {
  accounts: IPayoutAccountForm[];
  countryCode: string;
  onConfirmAccount: (newAccount: IPayoutAccountForm) => void;
  onRemoveAccount: (index: number) => void;
  onContinue: () => void;
  onBackToBusinessProfile: () => void;
}

export default function PayoutConfigurationStep(props: IPayoutConfigurationStepProps) {
  const { accounts, countryCode, onConfirmAccount, onRemoveAccount, onContinue, onBackToBusinessProfile } = props;

  const [values, setValues] = useState<IPayoutAccountForm>(defaultPayoutAccount);

  function updateField(field: keyof IPayoutAccountForm, value: string | boolean) {
    setValues(prev => ({ ...prev, [field]: value }));
  }

  const normalizedCountryCode = countryCode?.toUpperCase();
  const isCanadianAccount = normalizedCountryCode === "CA";

  const hasAccounts = accounts.length > 0;
  const [isAdding, setIsAdding] = useState(!hasAccounts);

  const isShowingForm = !hasAccounts || isAdding;

  useEffect(() => {
    if (accounts.length === 0) {
      setIsAdding(true);
    }
  }, [accounts.length]);

  const [dirty, setDirty] = useState(defaultDirty);

  const isCurrencyValid = Boolean(values.currency);
  const isAccountNumberValid = isValidAccountNumber(values.accountNumber);
  const isTransitNumberValid = isValidTransitNumber(values.transitNumber);
  const isInstitutionNumberValid = isValidInstitutionNumber(values.institutionNumber);
  const isRoutingNumberValid = isValidRoutingNumber(values.routingNumber);

  const isConfirmDisabled =
    !isCurrencyValid ||
    !isAccountNumberValid ||
    (isCanadianAccount ? !isTransitNumberValid || !isInstitutionNumberValid : !isRoutingNumberValid);

  function handleConfirmAccount() {
    onConfirmAccount(values);
    setValues(defaultPayoutAccount);
    setDirty(defaultDirty);
    setIsAdding(false);
  }

  function handleToggleAddMore() {
    if (isAdding) {
      setIsAdding(false);
      setValues(defaultPayoutAccount);
      setDirty(defaultDirty);
      return;
    }

    setIsAdding(true);
  }

  function handleCancelAddMore() {
    if (hasAccounts) {
      setIsAdding(false);
      return;
    }

    onBackToBusinessProfile();
  }

  return (
    <Card title="Add bank accounts for payouts" subtitle="We'll send earnings you receive to this account.">
      {hasAccounts ? (
        <Card.Section noSeparator>
          <div style={{ marginTop: "-24px" }}>
            <hr />
          </div>
          <p className="text-md text-semibold text-gray-900 mb-3">Added bank accounts:</p>
          {accounts.map((account, index) => (
            <BankAccountDisplay
              key={`${account.accountNumber}-${account.currency}-${account.routingNumber}-${account.transitNumber}-${account.institutionNumber}-${index}`}
              accountNumber={account.accountNumber}
              currency={account.currency}
              bankAccountName={t("Bank Name")}
              isDefault={account.isDefault}
              onRemove={() => onRemoveAccount(index)}
            />
          ))}
          <button
            className="flex items-center gap-1 text-sm text-semibold text-gray-900 mt-4"
            type="button"
            onClick={handleToggleAddMore}
          >
           
            <img src="/public/images/icons/plus.svg" alt="plus" style={{ width: "25px" }}/>

            <span>Add more</span>
          </button>
        </Card.Section>
      ) : null}

      {isShowingForm && (
        <Card.Section noSeparator>
          <Card>
            <Card.Section noSeparator>
              <Form>
                <FormLayout>
                  <FormLayout.Group>
                    <SelectNew
                      id="tee-on-payments-currency"
                      label={t("Currency")}
                      value={values?.currency}
                      error={dirty.currency && !isCurrencyValid}
                      onChange={e => {
                        const nextCurrency = e;
                        setDirty(prev => ({ ...prev, currency: prev.currency || Boolean(nextCurrency) }));
                        updateField("currency", nextCurrency);
                      }}
                      placeholder={""}
                      options={[
                        { label: t("CAD - Canadian dollar"), id: "CAD" },
                        { label: t("USD - United States dollar"), id: "USD" },
                      ]}
                      helpText={t("Payments will be made in this currency.")}
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <Input
                      value={values.accountNumber}
                      label="Account number"
                      id="account_number"
                      error={dirty.accountNumber && !isAccountNumberValid}
                      onChange={e => {
                        const next = e.target.value;
                        setDirty(prev => ({ ...prev, accountNumber: prev.accountNumber || next.trim().length > 0 }));
                        updateField("accountNumber", next);
                      }}
                      placeholder="Enter 7-12 digit account number"
                      helpText={
                        dirty.accountNumber && !isAccountNumberValid
                          ? t("Account number must be 7-12 digits")
                          : undefined
                      }
                      maxLength={12}
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    {!isCanadianAccount ? (
                      <Input
                        value={values.routingNumber}
                        label="Routing number"
                        id="routing_number"
                        error={dirty.routingNumber && !isRoutingNumberValid}
                        onChange={e => {
                          const next = e.target.value;
                          setDirty(prev => ({ ...prev, routingNumber: prev.routingNumber || next.trim().length > 0 }));
                          updateField("routingNumber", next);
                        }}
                        placeholder="Enter 9 digit routing number"
                        helpText={
                          dirty.routingNumber && !isRoutingNumberValid
                            ? t("Routing number must be exactly 9 digits")
                            : undefined
                        }
                        maxLength={9}
                      />
                    ) : (
                      <div className="grid grid-cols-2 gap-[2rem] ">
                        <div className="w-full">
                          <Input
                            value={values.transitNumber}
                            label="Transit number"
                            id="transit_number"
                            error={dirty.transitNumber && !isTransitNumberValid}
                            onChange={e => {
                              const next = e.target.value;
                              setDirty(prev => ({
                                ...prev,
                                transitNumber: prev.transitNumber || next.trim().length > 0,
                              }));
                              updateField("transitNumber", next);
                            }}
                            placeholder="E.g. 12345"
                            helpText={
                              dirty.transitNumber && !isTransitNumberValid
                                ? t("Transit number must be exactly 5 digits")
                                : undefined
                            }
                            maxLength={5}
                          />
                        </div>
                        <div className="w-full">
                          <Input
                            value={values.institutionNumber}
                            label="Institution number"
                            id="institution_number"
                            error={dirty.institutionNumber && !isInstitutionNumberValid}
                            onChange={e => {
                              const next = e.target.value;
                              setDirty(prev => ({
                                ...prev,
                                institutionNumber: prev.institutionNumber || next.trim().length > 0,
                              }));
                              updateField("institutionNumber", next);
                            }}
                            placeholder="E.g. 010"
                            helpText={
                              dirty.institutionNumber && !isInstitutionNumberValid
                                ? t("Institution number must be exactly 3 digits")
                                : undefined
                            }
                            maxLength={3}
                          />
                        </div>
                      </div>
                    )}
                 
                  
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <Checkbox
                      id="set_default"
                      size="medium"
                      checked={values.isDefault}
                      onChange={e => updateField("isDefault", e.target.checked)}
                      label="Set as default"
                    />
                  </FormLayout.Group>
                </FormLayout>
              </Form>

              {!hasAccounts ? (
                <div className="mt-3 flex w-full items-center justify-between">
                  <Button type="link" onClick={onContinue}>
                    {t("Skip this step")}
                  </Button>
                  <div className="flex items-center gap-1">
                    <Button type="secondary" onClick={handleCancelAddMore}>
                      {t("Cancel")}
                    </Button>
                    <Button type="primary" onClick={handleConfirmAccount} disabled={isConfirmDisabled}>
                      {t("Confirm bank account")}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex justify-end gap-3 mt-4">
                  <Button type="secondary" onClick={handleCancelAddMore}>
                    {t("Cancel")}
                  </Button>
                  <Button type="primary" onClick={handleConfirmAccount} disabled={isConfirmDisabled}>
                    {t("Confirm bank account")}
                  </Button>
                </div>
              )}
            </Card.Section>
          </Card>
        </Card.Section>
      )}

      {hasAccounts && (
        <Card.Section noSeparator>
          <Button type="primary" className="w-full justify-center" onClick={onContinue} disabled={!hasAccounts}>
            {t("Save and continue")}
          </Button>

          <div className="mt-3">
            <Button type="secondary" className="w-full justify-center" onClick={onBackToBusinessProfile}>
              {t("Cancel")}
            </Button>
          </div>
        </Card.Section>
      )}
    </Card>
  );
}
